// src/metaTags.prod.ts
// SEO tags (title, description, OG/Twitter cards, canonical, JSON-LD) and the
// Google Analytics loader are injected on production only. On the dev box and
// on a workstation the head carries nothing but the noindex directive the
// server stamps in — see modules/seoMeta.js and utils/seoEnv.ts.
//
// The font/icon stylesheets below are NOT SEO and load in every environment;
// dropping them outside production would leave dev without icons.
import { useHead } from '@vueuse/head'
import { isSeoEnabled } from '@/utils/seoEnv'

// Fonts + icon sets — required for the UI to render, every environment.
const ASSET_LINKS = [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
  { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap' },
  { rel: 'stylesheet', href: 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css' },
  { rel: 'stylesheet', href: 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css' },
  { rel: 'stylesheet', href: 'https://cdn.jsdelivr.net/npm/bootstrap-icons/font/bootstrap-icons.css' },
  { rel: 'stylesheet', href: 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.0-alpha3/dist/css/bootstrap.min.css' },
]

export function useProductionMetaTags() {
  if (!isSeoEnabled()) {
    // Non-production: assets only. No title, no meta, no JSON-LD.
    useHead({ link: ASSET_LINKS })
    return
  }

  useHead({
    title: 'Renants — Your Indian Community Marketplace | Rentals, Jobs & Services Free',
    meta: [
      { name: 'description', content: 'Find roommates, rentals, jobs and services for the Indian community in the USA. Browse listings or post your own — completely free, always.' },
      { name: 'keywords', content: 'Renants, Renants USA, Renants US, Indian community USA, NRI network, Indian network, Indian classifieds, Indian buy and sell, Indian yellow pages, Indian forums, discussion boards, Indian expressions, India news, Indian events, Indian tickets, Indian movies, Indian food, Indian recipes, Indian education, Indian real estate, Indian business, Indian articles, Indian cars, post classifieds, buy sell USA, Indian roommates, Indian jobs, Indian services, Indian nannies, Indian daycare.' },
      { name: 'distribution', content: 'global' },
      { name: 'copyright', content: '© Renants' },
      { property: 'og:title', content: 'Renants — Your Indian Community Marketplace' },
      { property: 'og:description', content: 'Find roommates, rentals, jobs and services for the Indian community in the USA. Browse listings or post your own — completely free, always.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://renants.com/' },
      { property: 'og:image', content: 'https://renants.com/og-preview.png' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:site_name', content: 'Renants' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Renants — Your Indian Community Marketplace' },
      { name: 'twitter:description', content: 'Find roommates, rentals, jobs and services for the Indian community in the USA. Completely free, always.' },
      { name: 'twitter:image', content: 'https://renants.com/og-preview.png' },
      { name: 'google-site-verification', content: 'ALK0wl09NLiw3rkw-Vi0B7ytcBrzEdXB8K32c_9YSSY' },
    ],
    link: [
      // Canonical is set dynamically per-page for citywise pages (e.g., /find-a-room/new-york-city)
      { rel: 'canonical', href: 'https://renants.com/' },
      ...ASSET_LINKS,
    ],
    script: [
      {
        src: `https://www.googletagmanager.com/gtag/js?id=${import.meta.env.VITE_GA_MEASUREMENT_ID}`,
        async: true
      },

      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://renants.com/#organization",
          "name": "Renants",
          "url": "https://renants.com/",
          "logo": "https://renants.com/assets/Logo-DemF8d4i.svg",
          "email": "Info@renants.com",
          "telephone": "+1-347-309-5344",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "US",
            "addressRegion": "NJ"
          },
          "areaServed": {
            "@type": "AdministrativeArea",
            "name": "New Jersey"
          }
        })
      },
      // LocalBusiness JSON-LD
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://renants.com/#localbusiness",
          "name": "Renants",
          "url": "https://renants.com/",
          "logo": "https://renants.com/assets/Logo-DemF8d4i.svg",
          "telephone": "+1-347-309-5344",
          "email": "Info@renants.com",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "US",
            "addressRegion": "NJ"
          },
          "areaServed": {
            "@type": "AdministrativeArea",
            "name": "New Jersey"
          }
        })
      },
      // WebSite JSON-LD
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://renants.com/#website",
          "name": "Renants",
          "url": "https://renants.com/",
          "publisher": {
            "@id": "https://renants.com/#organization"
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://renants.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })
      }
    ]
  })
}

// IMPORTANT: Call useProductionMetaTags() in your root component (e.g., App.vue or main layout)
